/**
 * A recipe line — three short lines, 480-friendly (`builder-engine-and-
 * consistency.md` §6.3, board cb-04 cells 2–3; phone cb-22 cell 1).
 *
 *   1. kind tile · name · Optional · the line's total · ⋯
 *   2. Base [0.2 h] + [0.004 h] per sq ft          (a rate of 0 is drawn faint)
 *   3. = 4.2 h at 1,000 sq ft            [3 tiers] [🔒 cost $508.20 · 41%]
 *
 * Props only. Every figure is handed in — the line's total, cost and margin
 * come from the server's preview — and there is no per-sq-ft/once select:
 * *once* is a rate of 0. The ⋯ opens the Line popover, where the flags and the
 * tiers live.
 */
import type { MouseEvent } from 'react'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { IconChevronRight, IconLock, IconMoreVertical } from '../../frame/icons.js'
import { KindTile, type TileKind } from '../kind-tile.js'
import {
  LINE_COST_CHIP, OPTIONAL_LABEL, PER_UNIT, QTY_BASE_LABEL, TIERS_CHIP, WHATEVER_THE_SIZE,
} from '../copy.js'
import { UnitInput } from '../quantity/unit-input.js'

export interface RecipeRowProps {
  kind: TileKind
  name: string
  optional?: boolean
  /** "$856.80" — the server's, at the sheet's sample. `null` while it is being worked out. */
  total: string | null
  base: string
  rate: string
  onBase: (value: string) => void
  onRate: (value: string) => void
  qtyUnit: string
  /** The measurement type's unit; `null` is a service with No measure — the base is the quantity. */
  measureUnit: string | null
  /** "= 4.2 h at 1,000 sq ft", or `null` for a rate of 0 ("= whatever the size"). */
  resolves: string | null
  /** Only on a line that has tiers. */
  tierCount?: number
  onTiers?: (event: MouseEvent<HTMLElement>) => void
  cost: string | null
  marginPct: number | null
  underTarget: boolean
  onMenu: (event: MouseEvent<HTMLElement>) => void
}

export function RecipeRow(props: RecipeRowProps) {
  return usePhone() ? <PhoneRecipeRow {...props} /> : <DesktopRecipeRow {...props} />
}

function CostChip({ cost, marginPct, underTarget }: Pick<RecipeRowProps, 'cost' | 'marginPct' | 'underTarget'>) {
  if (cost === null) return null
  return (
    <span className="tnum inline-flex h-6 items-center gap-[5px] whitespace-nowrap rounded-[7px] border border-line-faint bg-surface-alt px-2 text-[11.5px] text-text-muted">
      <span className="text-text-faint"><IconLock size={11} /></span>
      {LINE_COST_CHIP(cost)}
      {marginPct !== null && (
        <>
          <span className="text-text-faint">·</span>
          <b className={underTarget ? 'text-warn' : 'text-text-muted'}>{Math.round(marginPct)}%</b>
        </>
      )}
    </span>
  )
}

function DesktopRecipeRow({
  kind, name, optional, total, base, rate, onBase, onRate, qtyUnit, measureUnit, resolves,
  tierCount, onTiers, cost, marginPct, underTarget, onMenu,
}: RecipeRowProps) {
  const once = rate.trim() === '' || Number(rate) === 0
  return (
    <div data-recipe-row="" className="flex items-start gap-2.5 border-t border-line-faint py-[11px]">
      <KindTile kind={kind} size={28} />
      <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
        <div className="flex items-center gap-2">
          <b className="truncate text-[13.5px] font-semibold text-text">{name}</b>
          {optional && (
            <span className="inline-flex h-[18px] items-center rounded-[4px] bg-line-soft px-1.5 text-[10.5px] font-semibold text-text-muted">
              {OPTIONAL_LABEL}
            </span>
          )}
          <b className="tnum ml-auto text-[13.5px] text-text">{total ?? '—'}</b>
          <button type="button" aria-label={`${name} options`} onClick={onMenu}
            className="flex text-text-faint hover:text-text">
            <IconMoreVertical size={15} />
          </button>
        </div>

        <div className="flex items-center gap-[7px]">
          <span className="text-[11.5px] text-text-muted">{QTY_BASE_LABEL}</span>
          <UnitInput label={`${name} base`} value={base} onChange={onBase} unit={qtyUnit}
            size="small" width={76} align="right" />
          {measureUnit && (
            <>
              <span className="text-[12px] text-text-faint">+</span>
              <UnitInput label={`${name} ${PER_UNIT(measureUnit)}`} value={rate} onChange={onRate}
                unit={qtyUnit} size="small" width={84} align="right" dim={once} />
              <span className="whitespace-nowrap text-[12px] text-text-muted">{PER_UNIT(measureUnit)}</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {measureUnit && (
            <span className="tnum text-[11.5px] text-text-faint">{resolves ?? WHATEVER_THE_SIZE}</span>
          )}
          <span className="ml-auto inline-flex items-center gap-1.5">
            {tierCount !== undefined && tierCount > 0 && (
              <button type="button" onClick={onTiers}
                className="inline-flex h-[18px] items-center whitespace-nowrap rounded-[6px] border border-accent-line bg-accent-soft px-[7px] text-[10.5px] font-semibold text-accent-dark">
                {TIERS_CHIP(tierCount)}
              </button>
            )}
            <CostChip cost={cost} marginPct={marginPct} underTarget={underTarget} />
          </span>
        </div>
      </div>
    </div>
  )
}

/** cb-22 · 1: the row is a button onto the line's sheet; the rule reads as one line. */
function PhoneRecipeRow({
  kind, name, total, base, rate, qtyUnit, measureUnit, resolves, tierCount, cost, marginPct,
  underTarget, onMenu,
}: RecipeRowProps) {
  const once = rate.trim() === '' || Number(rate) === 0
  const rule = !measureUnit
    ? `${base} ${qtyUnit}`
    : once
      ? `${base} ${qtyUnit}, ${WHATEVER_THE_SIZE.replace('= ', '')}`
      : Number(base) === 0
        ? `${rate} ${qtyUnit} ${PER_UNIT(measureUnit)}`
        : `${base} ${qtyUnit} + ${rate} ${qtyUnit} ${PER_UNIT(measureUnit)}`
  return (
    <button type="button" data-recipe-row="" onClick={onMenu}
      className="flex w-full items-start gap-3 border-t border-line-faint py-3 text-left">
      <KindTile kind={kind} size={32} />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-center gap-2">
          <b className="truncate text-[14.5px] font-semibold text-text">{name}</b>
          <b className="tnum ml-auto text-[14.5px] text-text">{total ?? '—'}</b>
        </span>
        <span className="text-[13px] text-text">{rule}</span>
        <span className="flex items-center gap-1.5">
          {measureUnit && <span className="tnum text-[12px] text-text-faint">{resolves ?? WHATEVER_THE_SIZE}</span>}
          <span className="ml-auto inline-flex items-center gap-1.5">
            {tierCount !== undefined && tierCount > 0 && (
              <span className="inline-flex h-[18px] items-center rounded-[6px] border border-accent-line bg-accent-soft px-[7px] text-[10.5px] font-semibold text-accent-dark">
                {TIERS_CHIP(tierCount)}
              </span>
            )}
            {cost !== null && marginPct !== null && (
              <span className={cn('tnum inline-flex items-center gap-1 text-[11.5px]',
                underTarget ? 'text-warn' : 'text-text-muted')}>
                <IconLock size={11} />{Math.round(marginPct)}%
              </span>
            )}
          </span>
        </span>
      </span>
      <span className="mt-1 text-text-faint"><IconChevronRight size={16} /></span>
    </button>
  )
}
